import { useState } from "react";
import { X, Package, CheckCircle2, Info, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { errorMessage } from "../../lib/format";

export default function AddLeadOrderModal({ lead, isOpen, onClose, onCreated }) {
  const [description, setDescription] = useState("");
  const [product, setProduct] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [unit, setUnit] = useState("pcs");
  const [valuePrice, setValuePrice] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen || !lead) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const valParsed = valuePrice !== "" && !isNaN(valuePrice) ? parseFloat(valuePrice) : null;
      const qtyParsed = quantity ? parseFloat(quantity) : 1;
      const payload = {
        description: description.trim(),
        product: product.trim() || "Custom Item",
        quantity: qtyParsed,
        unit: unit.trim() || "pcs",
        value: valParsed,
        unit_price: valParsed !== null ? (valParsed / (qtyParsed > 0 ? qtyParsed : 1)) : 0,
      };

      await api("post", `/leads/${lead.lead_id}/orders`, payload);
      toast.success(`Draft order added to ${lead.company_name}`);

      if (onCreated) onCreated();
      onClose();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to add lead order"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="modal-backdrop"
      style={{
        zIndex: 10000,
        backgroundColor: "rgba(15, 23, 42, 0.6)",
        backdropFilter: "blur(4px)",
      }}
      data-testid="add-lead-order-modal-backdrop"
    >
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 540,
          width: "95%",
          padding: "24px 28px",
          borderRadius: 14,
          boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.25), 0 0 0 1px rgba(15, 23, 42, 0.06)",
        }}
        data-testid="add-lead-order-modal"
      >
        {/* Header */}
        <div className="modal-head" style={{ marginBottom: 16, alignItems: "flex-start" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                border: "1px solid #bfdbfe",
                color: "#2563eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 6px rgba(37, 99, 235, 0.12)",
                flexShrink: 0,
              }}
            >
              <Package size={20} />
            </div>
            <div>
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: "#2563eb",
                  letterSpacing: "0.6px",
                  textTransform: "uppercase",
                  marginBottom: 2,
                }}
              >
                LEAD ORDER
              </div>
              <h2
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#0f172a",
                  letterSpacing: "-0.3px",
                }}
              >
                Add Single Order
              </h2>
              <div
                style={{
                  fontSize: 12,
                  color: "#64748b",
                  marginTop: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span>Lead:</span>
                <span
                  style={{
                    fontWeight: 600,
                    color: "#1e293b",
                    background: "#f1f5f9",
                    padding: "2px 8px",
                    borderRadius: 4,
                  }}
                >
                  {lead.company_name}
                </span>
                <span style={{ fontSize: 11, color: "#94a3b8" }}>({lead.lead_id})</span>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            data-testid="close-add-lead-order-modal"
            style={{
              padding: 6,
              borderRadius: 6,
              color: "#64748b",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              cursor: "pointer",
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Lead Order Separation Notice */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            padding: "11px 14px",
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: 8,
            color: "#166534",
            fontSize: 12.5,
            lineHeight: 1.45,
            marginBottom: 20,
          }}
        >
          <Info size={17} style={{ flexShrink: 0, marginTop: 1, color: "#16a34a" }} />
          <div>
            <b style={{ color: "#14532d" }}>Draft Lead Order:</b> This order stays attached to this
            Lead and will automatically move to the main Orders section once the Lead is converted to
            a Client.
          </div>
        </div>

        {/* Standard Order Form: Exactly 5 fields in this order */}
        <form onSubmit={handleSubmit} data-testid="lead-single-order-form">
          <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 22 }}>
            {/* 1. Description */}
            <div>
              <label
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: "#1e293b",
                  marginBottom: 6,
                }}
              >
                <span>1. Description</span>
                <span style={{ fontSize: 11, fontWeight: 500, color: "#94a3b8" }}>Optional</span>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. Matte finish 350 GSM cards"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                data-testid="lead-order-description-input"
                style={{
                  width: "100%",
                  height: 40,
                  fontSize: 13.5,
                  border: "1.5px solid #cbd5e1",
                  borderRadius: 8,
                  padding: "8px 12px",
                }}
              />
            </div>

            {/* 2. Product */}
            <div>
              <label
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: "#1e293b",
                  marginBottom: 6,
                }}
              >
                <span>2. Product</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#dc2626" }}>*</span>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. Visiting Cards, Packaging Box, Brochure"
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                required
                data-testid="lead-order-product-input"
                style={{
                  width: "100%",
                  height: 40,
                  fontSize: 13.5,
                  border: "1.5px solid #cbd5e1",
                  borderRadius: 8,
                  padding: "8px 12px",
                }}
              />
            </div>

            {/* 3 & 4. Quantity and Unit */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {/* 3. Quantity */}
              <div>
                <label
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: "#1e293b",
                    marginBottom: 6,
                  }}
                >
                  <span>3. Quantity</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#dc2626" }}>*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  step="any"
                  className="form-control"
                  placeholder="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  required
                  data-testid="lead-order-quantity-input"
                  style={{
                    width: "100%",
                    height: 40,
                    fontSize: 13.5,
                    border: "1.5px solid #cbd5e1",
                    borderRadius: 8,
                    padding: "8px 12px",
                  }}
                />
              </div>

              {/* 4. Unit */}
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: "#1e293b",
                    marginBottom: 6,
                  }}
                >
                  4. Unit
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="pcs, boxes, sets"
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  data-testid="lead-order-unit-input"
                  style={{
                    width: "100%",
                    height: 40,
                    fontSize: 13.5,
                    border: "1.5px solid #cbd5e1",
                    borderRadius: 8,
                    padding: "8px 12px",
                  }}
                />
              </div>
            </div>

            {/* 5. Value/Price (Optional) */}
            <div>
              <label
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: "#1e293b",
                  marginBottom: 6,
                }}
              >
                <span>5. Value/Price (Optional)</span>
                <span style={{ fontSize: 11, fontWeight: 500, color: "#94a3b8" }}>Optional</span>
              </label>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  border: "1.5px solid #cbd5e1",
                  borderRadius: 8,
                  backgroundColor: "#ffffff",
                  overflow: "hidden",
                  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                }}
                className="price-input-group"
              >
                <span
                  style={{
                    padding: "0 12px",
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#f8fafc",
                    borderRight: "1.5px solid #cbd5e1",
                    color: "#64748b",
                    fontSize: 13.5,
                    fontWeight: 600,
                  }}
                >
                  ₹
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Optional (e.g., 2500)"
                  value={valuePrice}
                  onChange={(e) => setValuePrice(e.target.value)}
                  data-testid="lead-order-price-input"
                  style={{
                    width: "100%",
                    height: 40,
                    border: "none",
                    outline: "none",
                    padding: "8px 12px",
                    fontSize: 13.5,
                    color: "#0f172a",
                    background: "transparent",
                  }}
                />
              </div>
              <small
                style={{
                  fontSize: 11.5,
                  color: "#64748b",
                  marginTop: 5,
                  display: "block",
                }}
              >
                Value/Price is optional for lead orders and can remain blank.
              </small>
            </div>
          </div>

          {/* Modal Actions */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 10,
              paddingTop: 16,
              borderTop: "1px solid #e2e8f0",
            }}
          >
            <button
              type="button"
              className="outline-btn"
              onClick={onClose}
              disabled={submitting}
              data-testid="lead-order-cancel-btn"
              style={{
                height: 38,
                fontSize: 13,
                fontWeight: 600,
                padding: "0 18px",
                borderRadius: 8,
                border: "1.5px solid #cbd5e1",
                background: "#ffffff",
                color: "#475569",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              disabled={submitting}
              data-testid="lead-order-save-btn"
              style={{
                height: 38,
                fontSize: 13,
                fontWeight: 600,
                padding: "0 20px",
                borderRadius: 8,
                background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                border: "none",
                color: "#ffffff",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                boxShadow: "0 2px 8px rgba(37, 99, 235, 0.25)",
                cursor: submitting ? "not-allowed" : "pointer",
              }}
            >
              <CheckCircle2 size={16} />
              {submitting ? "Adding Order..." : "Add Order"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
